import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from './constants/url';
import AuthProvider from './contexts/AuthContext';
import useAuthStore from 'store/useAuthStore';
import { getAccessTokenFromCookies } from 'lib/utils/setTokensInCookies';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { customAppTheme } from 'components/Layouts/AppTheme';
import useUserStore from 'store/useUserStore';
import './App.css';

export default function App() {
  const { setIsLoggedIn } = useAuthStore(); // Zustand에서 로그인 상태를 가져옴
  const accessToken = getAccessTokenFromCookies(); // 쿠키에서 accessToken 가져오기
  const navigate = useNavigate();
  const location = useLocation(); // 현재 경로를 가져오기 위해 useLocation 사용

  // 로그아웃 또는 로그인 상태 변경 시 리디렉션 처리
  useEffect(() => {
    const publicPaths = [
      ROUTES.SIGNUP.path,
      ROUTES.FINDACCOUNT.path,
      ROUTES.FIND_PASSWORD_RESET.path,
      ROUTES.TERM_SERVICE.path,
      ROUTES.PRIVACY_POLICY.path,
      ROUTES.PUBLIC_VISIT_REQUEST.path,
      ROUTES.VIDEO.path
    ];

    const isPublicPath = publicPaths.some((path) => {
      if (path.includes(':')) {
        // 동적 경로 정규식 처리
        const dynamicPathPattern = new RegExp(
          `^${path.replace(/:\w+/g, '[^/]+').replace(/\/$/, '')}$` // 마지막 "/"" 처리
        );
        return dynamicPathPattern.test(location.pathname);
      }

      // 정적 경로면 바로 비교
      return path === location.pathname;
    });

    // accessToken이 존재하면 로그인 상태로 전환
    if (accessToken && !useAuthStore.getState().isLoggedIn) {
      // 로그인이 되어있지 않다면 Zustand 상태를 업데이트
      setIsLoggedIn(true);
      // 서버에서 사용자 정보를 가져와서 상태를 업데이트하는 로직이 필요할 수도 있음.
    }

    // 로그인되지 않은 상태 && 접근 불가능한 경로일 경우 로그인 페이지로 리디렉션
    if (!useAuthStore.getState().isLoggedIn && !accessToken && !isPublicPath) {
      navigate(ROUTES.LOGIN.path);
    }
  }, [useAuthStore.getState().isLoggedIn, navigate, accessToken, setIsLoggedIn, location.pathname]);

  // 관리자 전용 라우트 컴포넌트
  const AdminRoute = ({ element }: { element: React.ReactElement }) => {
    const { user } = useUserStore();
    const isAdmin = user?.groups.some((group) => group.type === 'SYSTEM_ADMIN');

    // 관리자가 아니라면 리디렉션
    if (!isAdmin) {
      return <Navigate to="*" />;
    }

    // 관리자인 경우 요청한 페이지 표시
    return element;
  };

  return (
    <ThemeProvider theme={customAppTheme}>
      <CssBaseline />

      <AuthProvider>
        <Routes>
          {/* 로그인하지 않은 사용자가 접근할 수 있는 경로 */}
          {!useAuthStore.getState().isLoggedIn && !accessToken ? (
            <>
              <Route path={ROUTES.LOGIN.path} element={ROUTES.LOGIN.element} />
              <Route path={ROUTES.SIGNUP.path} element={ROUTES.SIGNUP.element} />
              <Route path={ROUTES.FINDACCOUNT.path} element={ROUTES.FINDACCOUNT.element} />
              <Route path={ROUTES.FIND_PASSWORD_RESET.path} element={ROUTES.FIND_PASSWORD_RESET.element} />
              <Route path={ROUTES.TERM_SERVICE.path} element={ROUTES.TERM_SERVICE.element} />
              <Route path={ROUTES.PRIVACY_POLICY.path} element={ROUTES.PRIVACY_POLICY.element} />
              <Route path={ROUTES.PUBLIC_VISIT_REQUEST.path} element={ROUTES.PUBLIC_VISIT_REQUEST.element} />
              <Route path={ROUTES.VIDEO.path} element={ROUTES.VIDEO.element} />
            </>
          ) : (
            <>
              {Object.values(ROUTES).map((route) =>
                route.path === '/notice/new' ? (
                  // 관리자 전용 경로
                  <Route key={route.path} path={route.path} element={<AdminRoute element={route.element} />} />
                ) : (
                  <Route key={route.path} path={route.path} element={route.element} />
                )
              )}
            </>
          )}

          {/* 로그인한 사용자가 아닌 경우, 특정 페이지에 접근 시 로그인 페이지로 리디렉션 */}
          <Route path="*" element={<Navigate to={useAuthStore.getState().isLoggedIn ? ROUTES.NOT_FOUND.path : ROUTES.LOGIN.path} />} />
        </Routes>
      </AuthProvider>
    </ThemeProvider>
  );
}
